import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type TeamFilters,
  type CardsSortMode,
  type TableSortMode,
  isFiltersActive,
} from '@/utils/teamFilters';

type Props = {
  filters: TeamFilters;
  onChange: (next: TeamFilters) => void;
  onClear: () => void;
  teams: Array<{ id: string; name: string }>;
  viewMode: 'cards' | 'table';
  totalCount: number;
  visibleCount: number;
};

export default function TeamFiltersBar({
  filters,
  onChange,
  onClear,
  teams,
  viewMode,
  totalCount,
  visibleCount,
}: Props) {
  const { t } = useTranslation();
  const active = isFiltersActive(filters);

  const set = <K extends keyof TeamFilters>(key: K, value: TeamFilters[K]) => {
    onChange({ ...filters, [key]: value });
  };

  const cardsSortOptions: CardsSortMode[] = ['alphabetical', 'sessions', 'createdAt'];
  const tableSortOptions: TableSortMode[] = ['alphabetical', 'createdAt', 'email', 'sessions'];

  const sortLabel = (s: CardsSortMode | TableSortMode): string => {
    switch (s) {
      case 'alphabetical':
        return t('teamManagement.filters.sortAlphabetical', 'Alfabético');
      case 'sessions':
        return t('teamManagement.filters.sortSessions', 'Nº sesiones');
      case 'createdAt':
        return t('teamManagement.filters.sortCreatedAt', 'Fecha de alta');
      case 'email':
        return t('teamManagement.filters.sortEmail', 'Email');
    }
  };

  return (
    <div className="mb-6 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={filters.search}
            onChange={(e) => set('search', e.target.value)}
            placeholder={t('teamManagement.filters.searchPlaceholder', 'Buscar por nombre o email')}
            className="pl-9"
            aria-label={t('teamManagement.filters.searchPlaceholder', 'Buscar por nombre o email')}
          />
        </div>

        {/* Team */}
        <Select value={filters.team} onValueChange={(v) => set('team', v as TeamFilters['team'])}>
          <SelectTrigger size="sm" className="min-w-[160px]" aria-label={t('teamManagement.filters.teamLabel', 'Equipo')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('teamManagement.filters.teamAll', 'Todos los equipos')}</SelectItem>
            {teams.map((team) => (
              <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
            ))}
            <SelectItem value="unassigned">{t('teamManagement.filters.teamUnassigned', 'Sin asignar')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Activity */}
        <Select value={filters.activity} onValueChange={(v) => set('activity', v as TeamFilters['activity'])}>
          <SelectTrigger size="sm" className="min-w-[150px]" aria-label={t('teamManagement.filters.activityLabel', 'Actividad')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('teamManagement.filters.activityAll', 'Toda actividad')}</SelectItem>
            <SelectItem value="with">{t('teamManagement.filters.activityWith', 'Con sesiones')}</SelectItem>
            <SelectItem value="without">{t('teamManagement.filters.activityWithout', 'Sin sesiones')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Tier */}
        <Select value={filters.tier} onValueChange={(v) => set('tier', v as TeamFilters['tier'])}>
          <SelectTrigger size="sm" className="min-w-[130px]" aria-label={t('teamManagement.filters.tierLabel', 'Tier')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('teamManagement.filters.tierAll', 'Todos los tiers')}</SelectItem>
            <SelectItem value="elite">{t('teamManagement.filters.tierElite', 'Elite')}</SelectItem>
            <SelectItem value="pro">{t('teamManagement.filters.tierPro', 'Pro')}</SelectItem>
            <SelectItem value="standard">{t('teamManagement.filters.tierStandard', 'Standard')}</SelectItem>
            <SelectItem value="unrated">{t('teamManagement.filters.tierUnrated', 'Sin puntuar')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Sort */}
        {viewMode === 'cards' ? (
          <Select value={filters.sortCards} onValueChange={(v) => set('sortCards', v as CardsSortMode)}>
            <SelectTrigger size="sm" className="min-w-[150px]" aria-label={t('teamManagement.filters.sortLabel', 'Ordenar')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {cardsSortOptions.map((s) => (
                <SelectItem key={s} value={s}>{sortLabel(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Select value={filters.sortTable} onValueChange={(v) => set('sortTable', v as TableSortMode)}>
            <SelectTrigger size="sm" className="min-w-[150px]" aria-label={t('teamManagement.filters.sortLabel', 'Ordenar')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tableSortOptions.map((s) => (
                <SelectItem key={s} value={s}>{sortLabel(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Clear */}
        {active && (
          <Button variant="ghost" size="sm" onClick={onClear} className="text-muted-foreground">
            <X className="size-3.5" />
            {t('teamManagement.filters.clearFilters', 'Limpiar filtros')}
          </Button>
        )}
      </div>

      {/* Counter */}
      <p className="text-xs text-muted-foreground">
        {t('teamManagement.filters.resultsCount', {
          count: visibleCount,
          visible: visibleCount,
          total: totalCount,
          defaultValue_one: '{{visible}} de {{total}} miembro',
          defaultValue_other: '{{visible}} de {{total}} miembros',
        })}
      </p>
    </div>
  );
}
